import React, { createElement, ReactNode, useEffect, useRef, useState } from 'react'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, Outlet } from 'react-router-dom'
import { useMediaQuery } from '@/hooks/use-media-query'
import { AuthGuard } from '@/auth/auth-guard'
import { AuthProvider } from '@/auth/auth-provider'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/toaster'
import { SidebarProvider } from '@/components/ui/sidebar'
import { Sidebar } from '@/components/layout/sidebar'
import { Header } from '@/components/layout/header'
import NotFound from '@/pages/NotFound'
import { routes, checkRoutePermission, isExternalRoutes } from '@/utils/routes-util'
import { ThemeProvider } from '@/contexts/theme-context'
import { DataTableContextProvider } from '@/contexts/DataTableContext'
import { WebSocketProvider, useWebSocket } from '@/contexts/WebSocketContext'
import { useAuthStore } from '@/store/auth'
import { cn } from '@/lib/utils'
import { LoadingContextProvider as LoadingPageProvider } from '@/contexts/LoaderPageContext'
import { LoaderProvider as LoadingProvider } from '@/contexts/LoaderContext'
import { initChatStore, useChatStore } from './store/chat'
import { api } from '@/lib/axios'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { useGoogleTranslate } from '@/hooks/use-google-translate'

// types
type ProtectedRouteProps = {
  isAllowed: boolean
  redirectTo: string
  children: Readonly<ReactNode>
}
function Main({ children }: { children: Readonly<ReactNode> }) {
  const { user } = useAuthStore()
  const { inited } = useChatStore()
  const containerRef = useRef<HTMLDivElement>(null)
  const isMdAndDown = useMediaQuery('(max-width:1123px)')
  const isNotExternalRoutes = !isExternalRoutes(location.pathname)

  const [showSidebar, setShowSidebar] = useState(false)

  useEffect(() => {
    if (!inited && user) initChatStore(user)
  }, [user])

  return (
    <div ref={containerRef} className={cn('relative min-h-screen z-[0] min-w-full', isNotExternalRoutes ? 'app-shell' : 'bg-background', isMdAndDown && isNotExternalRoutes && 'pb-10')}>
      {isNotExternalRoutes && <Sidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />}
      {isNotExternalRoutes && <Header isOpen={isNotExternalRoutes} onMenuClick={() => setShowSidebar(prev => !prev)} />}
      <div className={cn(isNotExternalRoutes && 'lgAndUp:pl-[338px]', isNotExternalRoutes && !isMdAndDown ? 'px-12 py-2' : isNotExternalRoutes && 'px-6 py-5')}>{children}</div>
    </div>
  )
}

function ProtectedRoute({ isAllowed, redirectTo = '/login', children }: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()

  if (!isAllowed || !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  return children
}

const ConditionalWebsocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const shouldUseWebsocket = !isExternalRoutes(location.pathname)

  return shouldUseWebsocket ? <WebSocketProvider>{children}</WebSocketProvider> : <>{children}</>
}

function App() {
  useGoogleTranslate()
  const { user, updateBalance } = useAuthStore()
  const shouldUseWebsocket = !isExternalRoutes(location.pathname)

  if (shouldUseWebsocket) {
    // eslint-disable-next-line
    const { setChannel } = useWebSocket()
    setChannel({ channelName: `${import.meta.env.VITE_APP_NAME}-${import.meta.env.VITE_NODE_ENV}-exam-consultant-${user?.ref}` })
    setChannel({ channelName: `${import.meta.env.VITE_APP_NAME}-${import.meta.env.VITE_NODE_ENV}-checkout-${user?.ref}` })
    setChannel({ channelName: `${import.meta.env.VITE_APP_NAME}-${import.meta.env.VITE_NODE_ENV}-signs-symptoms-${user?.ref}` })
    setChannel({ channelName: `${import.meta.env.VITE_APP_NAME}-${import.meta.env.VITE_NODE_ENV}-exam-analysis-${user?.ref}` })
  }

  const _fetchUserBalance = async () => {
    try {
      const response = await api.get('/', {
        params: {
          action: 'user-balance',
          userRef: user?.ref
        }
      })

      if (response.data?.statusCode === HTTP_STATUS_CODE.OK) updateBalance(response.data.body[0].balance)
    } catch (err) {
      console.error(`Unhandled error at @/App._fetchUserBalance. Details: ${err}`)
    }
  }

  useEffect(() => {
    if (shouldUseWebsocket) _fetchUserBalance()
  }, [location.pathname])

  return (
    <Routes>
      {routes.map(route => {
        const routeElement = route.component ? <Main>{createElement(route.component)}</Main> : <NotFound />

        if (!route.roles?.length) {
          return <Route key={route.name} path={route.path} element={routeElement} />
        }

        return (
          <Route
            key={route.name}
            path={route.path}
            element={
              <ProtectedRoute isAllowed={checkRoutePermission(user, route.roles)} redirectTo="/login">
                {routeElement}
              </ProtectedRoute>
            }
          />
        )
      })}

      <Route path="*" element={<Navigate to="/404" replace />} />
      <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
  )
}

export default function AppWrapper() {
  return (
    <Router>
      <ThemeProvider defaultTheme="light">
        <LoadingProvider>
          <LoadingPageProvider>
            <TooltipProvider delayDuration={0}>
              <AuthProvider>
                <AuthGuard>
                  <SidebarProvider>
                    <DataTableContextProvider>
                      <ConditionalWebsocketProvider>
                        <Toaster />
                        <App />
                      </ConditionalWebsocketProvider>
                    </DataTableContextProvider>
                  </SidebarProvider>
                </AuthGuard>
              </AuthProvider>
            </TooltipProvider>
          </LoadingPageProvider>
        </LoadingProvider>
      </ThemeProvider>
    </Router>
  )
}
